import React, { useEffect, useMemo } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import {
  KButton,
  KCheckbox,
  KDialog,
  KIcon,
  KMenuDropDown,
} from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import { setAlert } from "../../redux/actions/actions";
import {
  getConfigs,
  deleteConfig,
  downloadConfig,
} from "../../apiCalls/config/config";
import { formatDate } from "../../utils/timeUtils";
import KInfiniteTable from "../../components/table/KInfiniteTable";
import { useStyles } from "./styles";
import { useHistory } from "react-router";

export type Config = {
  id: string;
  hardwareType: string;
  simType: string;
  version: string;
  contentsBase64: string | null;
  tags: string[];
  description: string;
  allowedCustomers?: string[];
};

export const defaultConfig: Config = {
  id: "",
  hardwareType: "",
  simType: "",
  version: "",
  contentsBase64: null,
  tags: [],
  description: "",
  allowedCustomers: [],
};

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      updateMyData={props.updateMyData}
      hasFilter
      manualFilters
      filterTable={props.filterTable}
      hasStickyColumns={true}
      onSelectRows={props.onSelectRows}
      manualSortBy
      sortTable={props.sortTable}
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data === newProps.data;
  },
);

const Config: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const firstUpdate = React.useRef(true);
  const { CancelToken } = axios;

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      deleteModalOpen,
      reload,
      cancelTokenSource,
      filterBy,
      sortBy,
      showImei,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    deleteModalOpen: false,
    reload: false,
    cancelTokenSource: null,
    filterBy: "",
    sortBy: "",
    showImei: false,
  });

  const [configToDelete, setDeleteConfigState] = useSetState(defaultConfig);

  const handleDelete = async () => {
    setState({ deleteModalOpen: false, query: "" });
    const res = await deleteConfig(configToDelete.id);
    if (res.data) {
      dispatch(setAlert(`Config ${configToDelete.version} deleted`, "success"));
      setState({ reload: true });
    } else {
      dispatch(setAlert(res.error, "error"));
    }
    setDeleteConfigState(defaultConfig);
  };

  const toggleDeletePopup = () =>
    setState({ deleteModalOpen: !deleteModalOpen });

  const navigateToCreatePage = () => {
    history.push("/config/devices/config/create/editor");
  };

  const loadConfigPage = (query, page, filterBy, sortBy, startEmpty = true) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({ isNextPageLoading: true, cancelTokenSource: source });

    if (!showImei) {
      if (filterBy !== "") {
        filterBy = `${filterBy}&filter_no_imei=1`;
      } else {
        filterBy = "filter_no_imei=1";
      }
    }

    const request: any = getConfigs(
      query,
      page,
      source.token,
      filterBy,
      sortBy,
    );

    request
      .then((res) => {
        const previousArray = startEmpty ? [] : items;
        setState({
          items: [...previousArray].concat(
            ...res.data?.data.map((x) => ({
              id: x.id,
              hardwareType: x.hardware_type,
              version: x.version,
              created: formatDate(x.created_at),
              description: x.description,
              tags: x.tags || [],
              imei: x.imei || "",
              downloaded_at: formatDate(x.downloaded_at) || "",
              allowedCustomers: x?.allowed_customers,
              simType: x?.sim_type,
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: res.data.has_next_page,
          cancelToken: null,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () => {
    loadConfigPage(query, pagesLoaded + 1, filterBy, sortBy, false);
  };

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadConfigPage(query, 1, filterBy, sortBy);
    }
  }, [reload]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadConfigPage(query, 1, filterBy, sortBy);
  }, [query, filterBy, sortBy]);

  const columns = React.useMemo(() => {
    const arr: any[] = [];
    if (showImei) {
      arr.push({
        Header: "IMEI",
        accessor: "imei",
        defaultCanSort: true,
      });
      arr.push({
        disableFilters: true,
        Header: "Downloaded",
        accessor: "downloaded_at",
        defaultCanSort: true,
      });
    }
    return arr.concat([
      {
        Header: "ID",
        accessor: "id",
        disableFilters: true,
        defaultCanSort: true,
        minWidth: 100,
        width: "30%",
        id: "id",
      },
      {
        Header: "Name",
        accessor: "version",
        defaultCanSort: true,
        id: "version",
      },
      {
        Header: "Hardware Type",
        accessor: "hardwareType",
        defaultCanSort: true,
        id: "hardware_type",
      },
      {
        Header: "Sim type",
        accessor: "simType",
        defaultCanSort: true,
        id: "sim_type",
      },
      {
        Header: "Tags",
        id: "tags",
        defaultCanSort: false,
        accessor: (config: Config) => config.tags.join(", "),
      },
      {
        Header: "Description",
        accessor: "description",
        defaultCanSort: true,
        id: "description",
      },
      {
        Header: "Companies",
        id: "customer",
        defaultCanSort: false,
        accessor: (config: Config) => config?.allowedCustomers?.join(", "),
      },
      {
        Header: "Created",
        accessor: "created",
        defaultCanSort: true,
        id: "created_at",
        disableFilters: true,
      },
      {
        disableFilters: true,
        accessor: "actions",
        Cell: (row) => {
          return (
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "flex-end",
                marginRight: 14,
              }}
            >
              <KMenuDropDown
                menuItems={[
                  {
                    icon: "download",
                    title: "Download",
                    action: () => {
                      downloadConfig(row.row.original.id).then((res) => {
                        const href = window.URL.createObjectURL(res?.data);
                        const link = document.createElement("a");
                        link.href = href;
                        const name =
                          row.row.original.imei !== ""
                            ? `${row.row.original.version}_${row.row.original.imei}`
                            : `${row.row.original.version}`;
                        link.setAttribute("download", name);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      });
                    },
                  },
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () => {
                      const id = row.row.original?.id;
                      history.push(`/config/devices/config/${id}/editor`);
                    },
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    action: () => {
                      setState({
                        deleteModalOpen: true,
                      });
                      setDeleteConfigState(row.row.original);
                    },
                  },
                ]}
              />
            </div>
          );
        },
        width: 100,
        sticky: "right",
      },
    ]);
  }, [showImei]);

  const filterTable = (values: { id: string; value: string }[]): void => {
    const transformIdToFilter = (value: string): string => {
      switch (value) {
        case "hardware":
          return "filter_hardware_type";
        default:
          return `filter_${value}`;
      }
    };
    const parsedFilter: string = values
      .map((x) => `${transformIdToFilter(x.id)}=${x.value}`)
      .join("&");
    setState({ filterBy: parsedFilter });
  };

  const sortTable = (values: { id: string; desc: boolean }[]): void => {
    const transformIdToSort = (value: string, desc: boolean): string => {
      const order = desc ? "DESC" : "ASC";
      switch (value) {
        case "hardware":
          return `hardware_type ${order}`;
        default:
          return `${value} ${order}`;
      }
    };
    const parsedSort: string = values
      .map((x) => `${transformIdToSort(x.id, x.desc)}`)
      .join(",");
    setState({ sortBy: parsedSort });
  };

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleDeletePopup()}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the config{" "}
        <b>{configToDelete.version}</b>?
      </p>
    </KDialog>
  );

  const handleShowImeiChange = (e) => {
    setState({ showImei: e.target.checked, reload: true });
  };

  return (
    <>
      <div className={classes.ctaContainer}>
        <KCheckbox
          handleChange={handleShowImeiChange}
          name="showImei"
          label="Show Downloaded Configurations"
          checked={showImei}
        />
        <div style={{ flexGrow: 1 }} />
        <KButton
          theme="primary"
          children="Add Config"
          icon={<KIcon icon="plus" />}
          onClick={navigateToCreatePage}
          style={{ marginLeft: "auto" }}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          loadNextPage={loadNextPage}
          isNextPageLoading={isNextPageLoading}
          columns={columns}
          data={items}
          hasNextPage={hasNextPage}
          filterTable={filterTable}
          sortTable={sortTable}
        />
      </div>
      {deletePopup}
    </>
  );
};

export default Config;
